import { useState, useRef, useMemo, useEffect } from 'react';
import { EventInput } from '@amzn/red-velvet-api';
import {
  WritingAssistantState,
  RewriteRejection,
  MIN_AI_DESCRIPTION_LENGTH,
} from '../../utils/aiContentTypes';
import {
  useRewriteEventDescription,
  useGenerateDescriptionTips,
  useGenerateShortDescription,
} from './aiContent';
import { markdownToHtml, stripMarkdown } from '../../utils/markdownToHtml';
import { getTextLength, stripHtml } from '../../utils/htmlText';
import { isAbortError } from '../../utils/errorHelper';
import { FIELD_LIMITS } from '../../utils/fieldConstraints';
import { publishKatalMetric } from '../../components/katalAnalytics';
import { useErrorMessages } from '../localization/errorMessages';

interface UseWritingAssistantProps {
  eventDescription: string;
  setEventDescription: (html: string) => void;
  summarizedDescription: string;
  setSummarizedDescription: (value: string) => void;
  currentFields: EventInput;
  updateFieldHandler: (fields: EventInput) => void;
  metricPrefix: string;
  /** Available during edit-event flow; undefined while creating a new event. */
  eventId?: string;
}

export function useWritingAssistant({
  eventDescription,
  setEventDescription,
  summarizedDescription,
  setSummarizedDescription,
  currentFields,
  updateFieldHandler,
  metricPrefix,
  eventId,
}: UseWritingAssistantProps) {
  const { resolveError } = useErrorMessages();
  const [assistantState, setAssistantState] = useState<WritingAssistantState>('idle');
  const [rewrittenDescription, setRewrittenDescription] = useState('');
  const [rewriteRejection, setRewriteRejection] = useState<RewriteRejection>();
  const [tips, setTips] = useState('');
  const [tipsError, setTipsError] = useState(false);
  const [assistantError, setAssistantError] = useState<string | undefined>();
  const [isShortDescAiGenerated, setIsShortDescAiGenerated] = useState(false);
  const [shortDescError, setShortDescError] = useState(false);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [editorWidthPct, setEditorWidthPct] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController>();
  // The summary has its own button, so cancelling a rewrite must not stop it.
  const shortDescAbortControllerRef = useRef<AbortController>();

  const fieldsRef = useRef({ eventDescription, summarizedDescription });
  fieldsRef.current = { eventDescription, summarizedDescription };
  const currentFieldsRef = useRef(currentFields);
  currentFieldsRef.current = currentFields;

  const rewriteMutation = useRewriteEventDescription();
  const tipsMutation = useGenerateDescriptionTips();
  const shortDescMutation = useGenerateShortDescription();

  // Everything in flight is dropped, the summary included. React Query only runs the callbacks
  // passed to `mutate` while the hook is still mounted, so a summary that lands after the
  // coordinator leaves the step is never saved. Letting it run would bill a model call whose answer
  // nobody can use.
  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
      shortDescAbortControllerRef.current?.abort();
    },
    []
  );

  const descriptionPlainTextLength = useMemo(
    () => stripHtml(eventDescription).length,
    [eventDescription]
  );

  const canRewrite =
    descriptionPlainTextLength >= MIN_AI_DESCRIPTION_LENGTH &&
    descriptionPlainTextLength <= FIELD_LIMITS.EVENT_DESCRIPTION;

  // Tooltip explaining why the user cannot rewrite (undefined when rewrite is allowed)
  const rewriteDisabledReason = canRewrite
    ? undefined
    : descriptionPlainTextLength > FIELD_LIMITS.EVENT_DESCRIPTION
      ? 'writingAssistant.maxCharsHint'
      : 'writingAssistant.minCharsHint';

  const requestTips = (description: string, rewritten: string, abortSignal: AbortSignal) => {
    setTipsError(false);
    tipsMutation.mutate(
      {
        description,
        // The model is asked to keep the rewrite under the limit but can still write past it, and
        // the tips call rejects anything longer. Cutting the tail off gives the coordinator tips on
        // most of the rewrite instead of none at all.
        rewrittenDescription: rewritten.slice(0, FIELD_LIMITS.EVENT_DESCRIPTION),
        eventId,
        abortSignal,
      },
      {
        onSuccess: (data) => {
          // Aborting does not stop this callback, so old tips can still land here.
          if (abortSignal.aborted) return;
          setTips(data.tips ?? '');
        },
        onError: (error) => {
          // The coordinator cancelled, so there is no failure to tell them about.
          if (isAbortError(error)) return;
          setTipsError(true);
          publishKatalMetric(`${metricPrefix}_aiRewriteTipsFailed`, error.message);
        },
      }
    );
  };

  const handleRewrite = () => {
    const plainText = stripHtml(eventDescription);
    if (
      plainText.length < MIN_AI_DESCRIPTION_LENGTH ||
      plainText.length > FIELD_LIMITS.EVENT_DESCRIPTION
    )
      return;
    // The old tips are about a rewrite the coordinator will not see any more.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setAssistantState('loading');
    setAssistantError(undefined);
    setRewriteRejection(undefined);
    setTips('');
    setTipsError(false);
    publishKatalMetric(`${metricPrefix}_aiRewriteRequested`, String(plainText.length));
    rewriteMutation.mutate(
      { description: plainText, eventId, abortSignal: controller.signal },
      {
        onSuccess: (data) => {
          // Aborting does not stop this callback, so a cancelled rewrite could come back on screen.
          if (controller.signal.aborted) return;
          const rewritten = data.rewrittenDescription ?? '';
          // No flag means treat it as valid, so an older backend still works.
          const valid = data.valid !== false;
          setRewrittenDescription(rewritten);
          // The model is asked to stay under the description limit but can write past it. Accepting
          // a longer rewrite would replace what the coordinator wrote with text the wizard then
          // refuses to publish, leaving them to hand-trim AI prose to get moving again.
          const fits = getTextLength(markdownToHtml(rewritten)) <= FIELD_LIMITS.EVENT_DESCRIPTION;
          setRewriteRejection(
            !valid ? 'notAnEventDescription' : !fits ? 'tooLongForField' : undefined
          );
          setAssistantState('result');
          if (valid) {
            publishKatalMetric(`${metricPrefix}_aiRewriteGenerated`, String(rewritten.length));
            // A second call, so the coordinator can read the rewrite while the tips are written.
            if (rewritten) requestTips(plainText, rewritten, controller.signal);
          } else {
            publishKatalMetric(`${metricPrefix}_aiRewriteRejected`, eventId ?? 'Draft event');
          }
        },
        onError: (error) => {
          // The coordinator cancelled, so there is no failure to tell them about.
          if (isAbortError(error)) return;
          setAssistantError(resolveError(error));
          setAssistantState('error');
          publishKatalMetric(`${metricPrefix}_aiRewriteFailed`, error.message);
        },
      }
    );
  };

  const discardRewrite = () => {
    abortControllerRef.current?.abort();
    setAssistantState('idle');
    setRewrittenDescription('');
    setRewriteRejection(undefined);
    setTips('');
    setTipsError(false);
    setAssistantError(undefined);
  };

  const handleCancelGeneration = () => {
    discardRewrite();
    publishKatalMetric(`${metricPrefix}_aiRewriteGenerationCancelled`, eventId ?? 'Draft event');
  };

  const isRewriteValid = rewriteRejection === undefined;

  const handleAcceptRewrite = () => {
    // Accepting an empty or refused rewrite would wipe what the coordinator wrote.
    if (!isRewriteValid || !rewrittenDescription) return;
    const html = markdownToHtml(rewrittenDescription);
    setEventDescription(html);
    setAssistantState('tips');
    publishKatalMetric(`${metricPrefix}_aiRewriteAccepted`, eventId ?? 'Draft event');
    updateFieldHandler({
      ...currentFieldsRef.current,
      description: html,
      shortDescription: fieldsRef.current.summarizedDescription,
    });
  };

  const handleCancelRewrite = () => {
    discardRewrite();
    publishKatalMetric(`${metricPrefix}_aiRewriteCancelled`, eventId ?? 'Draft event');
  };

  const triggerShortDescGeneration = (descriptionText?: string) => {
    const plainText = descriptionText ?? stripHtml(eventDescription);
    if (
      plainText.length < MIN_AI_DESCRIPTION_LENGTH ||
      plainText.length > FIELD_LIMITS.EVENT_DESCRIPTION
    )
      return;
    setShortDescError(false);
    // A summary can take several tries, so an older call may finish after a newer one.
    shortDescAbortControllerRef.current?.abort();
    const controller = new AbortController();
    shortDescAbortControllerRef.current = controller;
    // If we already generated a short description in this session, the user is
    // asking for a different one. Tell the server so it can vary the output.
    const regenerate = isShortDescAiGenerated;
    publishKatalMetric(`${metricPrefix}_aiGenerateShortDescRequested`, String(plainText.length));
    shortDescMutation.mutate(
      {
        description: plainText,
        eventId,
        regenerate,
        abortSignal: controller.signal,
      },
      {
        onSuccess: (data) => {
          // Aborting does not stop this callback, and this one saves the draft, so an old summary
          // could be written over the new one.
          if (controller.signal.aborted) return;
          // Truncation is a safety measure to avoid exceeding the max character limit
          const shortDesc = stripMarkdown(data.shortDescription ?? '').slice(
            0,
            FIELD_LIMITS.SUMMARIZED_DESCRIPTION
          );
          setSummarizedDescription(shortDesc);
          setIsShortDescAiGenerated(true);
          updateFieldHandler({
            ...currentFieldsRef.current,
            description: fieldsRef.current.eventDescription,
            shortDescription: shortDesc,
          });
        },
        onError: (error) => {
          // A newer request replaced this one, so its failure is not worth a banner.
          if (isAbortError(error)) return;
          setShortDescError(true);
          publishKatalMetric(`${metricPrefix}_aiGenerateShortDescFailed`, error.message);
        },
      }
    );
  };

  const handleDescriptionChange = (html: string) => {
    setEventDescription(html);
    if (assistantState === 'tips') {
      // The tips being written are about the text before this edit.
      abortControllerRef.current?.abort();
      setAssistantState('idle');
    }
  };

  const handleTogglePanel = () => {
    const next = !isPanelCollapsed;
    setIsPanelCollapsed(next);
    publishKatalMetric(`${metricPrefix}_aiPanel${next ? 'Collapsed' : 'Expanded'}`, '');
  };

  const clearAiGeneratedLabel = () => setIsShortDescAiGenerated(false);

  return {
    assistantState,
    rewrittenDescription,
    isRewriteValid,
    rewriteRejection,
    tips,
    tipsError,
    isTipsPending: tipsMutation.isPending,
    assistantError,
    canRewrite,
    rewriteDisabledReason,
    isPanelCollapsed,
    editorWidthPct,
    containerRef,
    isShortDescAiGenerated,
    shortDescError,
    isGeneratingShortDesc: shortDescMutation.isPending,
    setEditorWidthPct,
    handleRewrite,
    handleAcceptRewrite,
    handleCancelRewrite,
    handleCancelGeneration,
    handleDescriptionChange,
    handleTogglePanel,
    triggerShortDescGeneration,
    clearAiGeneratedLabel,
  };
}
